import { createSelector } from '@reduxjs/toolkit'
import _isEmpty from 'lodash/isEmpty'
import _head from 'lodash/head'
import _sortBy from 'lodash/sortBy'

import { makeFetchSelectors } from 'app/redux-fetch'
import {
  JOB_INFO_IMAGES_REDUCER_NAME,
  JOB_INFO_REDUCER_NAME,
  JOB_REDUCER_NAME,
  jobInitialContacts,
} from 'features/jobs/consts'
import type {
  JobContacts,
  JobLocation,
  JobImageData,
} from 'features/jobs/types'

export const jobInfoDataSelector = makeFetchSelectors(
  JOB_REDUCER_NAME,
  JOB_INFO_REDUCER_NAME
)

export const jobInfoSelector = createSelector(
  jobInfoDataSelector.data,
  (jobInfoData) => jobInfoData?.[0]
)

export const jobInfoNotesSelector = createSelector(
  jobInfoDataSelector.data,
  (jobInfoData) =>
    _sortBy(jobInfoData?.[0]?.notes || [], ['editDate']).reverse()
)

export const jobInfoContactsSelector = createSelector(
  jobInfoDataSelector.data,
  (jobInfoData) => {
    const jobContacts = jobInfoData?.[0]?.contacts?.[0]
    if (_isEmpty(jobContacts)) {
      return jobInitialContacts
    }

    return Object.fromEntries(
      Object.entries(jobInitialContacts).map(([key, value]) => [
        key,
        jobContacts[key] || value,
      ])
    ) as JobContacts
  }
)

export const jobInfoLocationSelector = createSelector(
  jobInfoDataSelector.data,
  (jobInfoData) => jobInfoData?.[0]?.location as JobLocation
)

export const jobInfoImagesDataSelector = makeFetchSelectors(
  JOB_REDUCER_NAME,
  JOB_INFO_IMAGES_REDUCER_NAME
)

export const jobInfoImagesThumbsSelector = createSelector(
  jobInfoImagesDataSelector.data,
  (jobInfoImagesData) =>
    jobInfoImagesData
      ?.filter(
        (imgData: JobImageData) =>
          imgData.metadata?.['resource-kind'] === 'thumb'
      )
      .sort(
        (a: JobImageData, b: JobImageData) =>
          b.date_updated && a.date_updated && b.date_updated - a.date_updated
      )
)

export const jobInfoImagesFullSizeSelector = createSelector(
  jobInfoImagesDataSelector.data,
  (jobInfoImagesData) =>
    jobInfoImagesData?.filter(
      (imgData: JobImageData) => imgData.metadata?.['resource-kind'] === 'full'
    )
)

export const jobInfoImageFullSelector = (fileSet: string) =>
  createSelector(
    jobInfoImagesFullSizeSelector,
    (jobInfoImages) =>
      _head(
        jobInfoImages?.filter(
          (imgData: JobImageData) => imgData.metadata?.['file-set'] === fileSet
        )
      ) as JobImageData | undefined
  )
